import './quests.css';
import './bestiary.css';

import {
  BIOME_IDS,
  BIOMES,
  type BiomeId,
  type BestiaryPageState,
  bestiaryOf,
  type ProfileEvent,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { isAction } from '../settings/keybindings.js';
import { bestiaryPage, type CreatureFrame } from './bestiary-page.js';
import { closeButton, hudShortcutButton } from './hud.js';
import { kitTitle } from './kit.js';
import { uiFormat, uiText } from './text.js';

export interface BestiaryPanelOptions {
  /** Item icons, for the loot seen. */
  icons: () => CssAtlas | null;
  /** A creature's idle frame, from the scene's textures. */
  creatureFrame: CreatureFrame;
  /** Ask the server for the career; its answer comes back through `setProfile`. */
  onRequest: () => void;
}

/**
 * The hunting log (ADR 0093): one page per creature of a region, region by
 * region, read from the career the `profile` answer carries. Kills, tiers and
 * loot seen are the server's; `bestiaryOf` only lays them out as pages, and
 * the gold frame of a page at its last tier is a drawing, not a rule.
 */
export class BestiaryPanel {
  private readonly root: HTMLElement;
  private readonly regions: HTMLElement;
  private readonly list: HTMLElement;
  private readonly foot: HTMLElement;
  private readonly button: HTMLButtonElement;
  private region: BiomeId = 'meadow';
  private pages: BestiaryPageState[] | null = null;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    if (event.key === 'Escape' && !this.root.hidden) {
      this.close();
      return;
    }
    if (!isAction(event, 'bestiary')) return;
    event.preventDefault();
    this.toggle();
  };

  constructor(private readonly options: BestiaryPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'bestiary';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Carnet de chasse'));

    const title = document.createElement('h2');
    title.className = 'bestiary__title';
    kitTitle(title, 'stat-kills');
    title.append(
      uiText('Carnet de chasse'),
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );

    this.regions = document.createElement('div');
    this.regions.className = 'bestiary__regions';
    this.regions.setAttribute('role', 'tablist');
    this.list = document.createElement('div');
    this.list.className = 'bestiary__list';
    this.foot = document.createElement('p');
    this.foot.className = 'bestiary__foot';
    this.root.append(title, this.regions, this.list, this.foot);
    document.body.append(this.root);

    this.button = hudShortcutButton('stat-kills', 'Carnet de chasse', 'bestiary');
    this.button.addEventListener('click', () => {
      this.toggle();
    });
    window.addEventListener('keydown', this.onKeyDown);
    this.render();
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    if (this.root.hidden) this.open();
    else this.close();
  }

  open(): void {
    this.root.hidden = false;
    // Asked afresh at every opening: the career moves with every blow.
    this.options.onRequest();
    this.render();
  }

  close(): void {
    this.root.hidden = true;
  }

  setProfile(profile: ProfileEvent): void {
    this.pages = bestiaryOf(profile.career);
    if (!this.root.hidden) this.render();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
    this.button.remove();
  }

  private choose(region: BiomeId): void {
    this.region = region;
    this.render();
    this.list.scrollTop = 0;
  }

  private render(): void {
    this.renderRegions();
    const pages = this.pages;
    if (pages === null) {
      const waiting = document.createElement('p');
      waiting.className = 'bestiary__empty';
      waiting.textContent = uiText('Chargement…');
      this.list.replaceChildren(waiting);
      this.foot.textContent = '';
      return;
    }
    const icons = this.options.icons();
    const shown = pages.filter((page) => page.biome === this.region);
    this.list.replaceChildren(
      ...shown.map((page) => bestiaryPage(page, this.options.creatureFrame, icons)),
    );
    this.foot.textContent = uiFormat('{seen} / {total} créatures rencontrées', {
      seen: pages.filter((page) => !page.hidden).length,
      total: pages.length,
    });
  }

  private renderRegions(): void {
    this.regions.replaceChildren(
      ...BIOME_IDS.map((biome) => {
        const { name } = BIOMES[biome];
        const met = this.pages?.filter((page) => page.biome === biome && !page.hidden).length;
        const total = this.pages?.filter((page) => page.biome === biome).length;
        const active = biome === this.region;
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'bestiary__region';
        tab.classList.toggle('bestiary__region--active', active);
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', String(active));
        tab.title = uiText(name);
        // Its first word on the tab (« Forêt », « Pics »): five tabs share a row.
        const label = document.createElement('span');
        label.className = 'bestiary__region-name';
        label.textContent = uiText(name.split(' ')[0] ?? name);
        tab.append(label);
        if (met !== undefined && total !== undefined) {
          const count = document.createElement('span');
          count.className = 'bestiary__region-count';
          count.textContent = `${String(met)}/${String(total)}`;
          tab.append(count);
        }
        tab.addEventListener('click', () => {
          this.choose(biome);
        });
        return tab;
      }),
    );
  }
}
